"use client";
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  Image as ChakraImage,
  Textarea,
  Slider,
  SliderMark,
  SliderTrack,
  createIcon,
  SliderFilledTrack,
  SliderThumb,
  Spinner,
  useToast,
  Icon,
  Modal,
  ModalOverlay,
  ModalBody,
  ModalContent,
} from "@chakra-ui/react";
import { useDropzone, FileRejection } from "react-dropzone";
import { useFalForLoraImgToImg, useFalForLoraImgToImgMask } from "queries";
import UploadRemixImageIcon from "@public/upload_remix_image_icon.svg";
import { IoMdClose } from "react-icons/io";
import RemixGalleryLayout from "./RemixGalleryLayout";
import {  RemixGalleryImage } from "types";
import { MaskingTool, AsyncDisplay } from "components";
import LoraSelector from "./Generator/LoraSelector";
import { useAppContext } from "hooks"; // Add this if not present


interface FluxPlaygroundRemixProps {
  prompt?: string;
  setPrompt: React.Dispatch<React.SetStateAction<string>>;
  modelUsername: string;
  constantLora: Lora;
  isGenerating: boolean;
  error: string | null;
  refetchTrigger?: () => void; 
  model: any
}



interface Lora {
  path: string;
  scale: number;
}

const FluxPlaygroundRemix: React.FC<FluxPlaygroundRemixProps> = ({  
  constantLora,
  refetchTrigger,
  model,
   }) => {
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [strength, setStrength] = useState(0.85);
  const [baseLoraScale, setBaseLoraScale] = useState(1.0);
  const [prompt, setPrompt] = useState("woman")
  const [customPromptOpen, setCustomPromptOpen] = useState(false);
  const [dynamicLoras, setDynamicLoras] = useState<{ url: string; weight: number; image?: string; title?: string }[]>([]);
  const [selectedLoraImage, setSelectedLoraImage] = useState<string>("");
  const [selectedLoraTitle, setSelectedLoraTitle] = useState<string>("");
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const { generateImage, loading: isGenerating, error } = useFalForLoraImgToImg()
  const [isMaskingToolOpen, setIsMaskingToolOpen] = useState(false); 
  const [maskedGeneratedImageUrl, setMaskedGeneratedImageUrl] = useState<string | null>(null);
  const { generateImage: generateImageWithMask, loading: isMaskGenerating } = useFalForLoraImgToImgMask();
  const [maskBase64, setMaskBase64] = useState<string | null>(null); 
   const [isGalleryOpen, setIsGalleryOpen] = useState(false);
   const [selectedImage, setSelectedImage] = useState<RemixGalleryImage | null>(null);
   const [activeRequests, setActiveRequests] = useState<number>(0); // ✅ Track active requests
   const [numGenerations, setNumGenerations] = useState<number>(1);
   const { profile } = useAppContext(); // Destructure profile from context
   const isInsufficientCredits = (profile?.credit ?? 0) < numGenerations;

   const handleOpenGallery = () => setIsGalleryOpen(true);
   const handleCloseGallery = () => setIsGalleryOpen(false);
   const handleAddLora = (
    triggerWords: string,
    loraData?: {
      path: string;
      scale: number;
      image?: string;
      title?: string;
    }
  ) => {
    if (dynamicLoras.length > 0) {
      toast({
        title: "Only one Style allowed",
        description: "You can only add one optional style.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const newPrompt = prompt.trim() + (prompt.trim() ? ", " : "") + triggerWords;
    setPrompt(newPrompt);

    if (loraData) {
      setDynamicLoras([
        {
          url: loraData.path,
          weight: loraData.scale || 0.5,
          image: loraData.image,
          title: loraData.title,
        },
      ]);
      if (loraData.image) setSelectedLoraImage(loraData.image);
      if (loraData.title) setSelectedLoraTitle(loraData.title);
    }
  };


    const handleRemoveLora = (index: number) => {
      setDynamicLoras(dynamicLoras.filter((_, i) => i !== index));
      setSelectedLoraImage("");
      setSelectedLoraTitle("");
    };
   // Update file and preview when an image is selected in the gallery
   useEffect(() => {
     if (selectedImage?.sas_url) {
       fetch(selectedImage.sas_url)
         .then((res) => res.blob())
         .then((blob) => {
           const imageFile = new File([blob], "gallery-image", { type: blob.type });
           setFile(imageFile);
           setPreview(URL.createObjectURL(blob));
         })
         .catch((err) => {
           console.error("Error fetching selected image:", err);
           toast({
             title: "Error",
             description: "Unable to load the selected image.",
             status: "error",
             duration: 5000,
             isClosable: true,
           });
         });
     }
   }, [selectedImage, toast]);

   useEffect(() => {
    if (generatedImageUrl) {
      setActiveRequests(0); // Stop loading state
    }
  }, [generatedImageUrl]);
  

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (acceptedFiles.length > 0) {
        const selectedFile = acceptedFiles[0];

       
      // Check image dimensions
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const aspectRatio = img.width / img.height;

          if (img.width < 512 || img.height < 512) {
            toast({
              title: "Image Too Small",
              description: "Image size must be at least 512x512 pixels.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          } else if (img.width > 2048 || img.height > 2048) {
            toast({
              title: "Image Too Large",
              description: "Image size must not exceed 2048x2048 px",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          } else if (aspectRatio > 2 || aspectRatio < 0.5) {
            toast({
              title: "Invalid Aspect Ratio",
              description: "Aspect ratio must be between 1:2 (portrait) and 2:1 (landscape).",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          } else {
            // Set file and preview if the image size is valid
            setFile(selectedFile);
            setPreview(URL.createObjectURL(selectedFile));
          }
        };
        if (event.target?.result) {
          img.src = event.target.result as string;
        }
      };
      reader.readAsDataURL(selectedFile);
    }


      fileRejections.forEach((rejection) => {
        toast({
          title: "File Error",
          description: rejection.errors[0].message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
    },
    accept: { "image/png": [], "image/jpeg": [], "image/webp": [], "image/heic": [] },
    maxSize: 3 * 1024 * 1024,
    multiple: false,
  });

  const handleGenerateRemixImage = async () => {

    if ((profile?.credit ?? 0) < numGenerations) {
      toast({
        title: "Insufficient Credits",
        description: `You need at least ${numGenerations} credit${numGenerations > 1 ? 's' : ''} to generate ${numGenerations} image${numGenerations > 1 ? 's' : ''}.`,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    

    if (!file || !prompt) {
      toast({
        title: "Input Required",
        description: "Please upload an image and enter a prompt.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    
  
    // ✅ Reset previously generated images before making a new request
    setGeneratedImageUrl(null);
  
    // ✅ Ensure placeholders are only added when clicking "Create"
    setActiveRequests((prev) => prev + numGenerations);
  
    const adjustedConstantLora = {
      ...constantLora,
      scale: baseLoraScale,
    };
  
    const adjustedDynamicLoras = dynamicLoras
      .filter((lora) => lora.url.trim() !== "")
      .map((lora) => ({
        path: lora.url.trim(),
        scale: lora.weight,
      }));
  
    try {
      let response;
  
      if (maskBase64) {
        // ✅ If using a mask, generate with mask
        response = await generateImageWithMask(
          file,
          maskBase64,
          prompt,
          adjustedConstantLora,
          strength,
          numGenerations,
          adjustedDynamicLoras
        );
      } else {
        // ✅ Otherwise, generate normally
        response = await generateImage(
          file,
          prompt,
          adjustedConstantLora,
          strength,
          numGenerations,
          undefined,
          adjustedDynamicLoras
        );
      }
  
      console.log("API Response:", response); // ✅ Debugging log
  
      if (response?.image_urls?.length) {
        setGeneratedImageUrl(response.image_urls[0]); // ✅ Store the first generated image
      } else {
        toast({
          title: "Error",
          description: "Failed to generate image.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error generating image:", error);
      toast({
        title: "Error",
        description: "An error occurred during generation.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      // ✅ Ensure `activeRequests` resets properly
      setActiveRequests(0);
    }
  
    // ✅ Trigger refetch only when generation is successful
    if (refetchTrigger) {
      refetchTrigger();
    }
  };
  
  
  const handlePaste = (event: ClipboardEvent) => {
    const clipboardItems = event.clipboardData?.items;
    if (!clipboardItems) return;

    for (const item of clipboardItems) {
      if (item.type.startsWith("image/")) {
        const pastedImage = item.getAsFile();
        if (pastedImage) {
          setFile(pastedImage);
          setPreview(URL.createObjectURL(pastedImage));
        }
        event.preventDefault();
        break;
      }
    }
  };

  useEffect(() => {
    // Add paste event listener
    window.addEventListener("paste", handlePaste);
    // Cleanup: remove the event listener on unmount
    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, []); // Em

  const handleOpenMaskingTool = () => {
    if (!preview) {
      toast({
        title: "Error",
        description: "Please upload an image first.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setIsMaskingToolOpen(true);
  };

  const handleMaskingToolClose = async (maskedImg: string | null, maskB64: string | null) => {
    setIsMaskingToolOpen(false); 
  
    if (maskB64 && file) {
      const img = new Image();
      img.src = preview!;
      img.onload = async () => {
        // Calculate dimensions divisible by 8
        const adjustedWidth = Math.floor(img.naturalWidth / 8) * 8;
        const adjustedHeight = Math.floor(img.naturalHeight / 8) * 8;
  
        const canvas = document.createElement("canvas");
        canvas.width = adjustedWidth;
        canvas.height = adjustedHeight;
  
        const ctx = canvas.getContext("2d");
        if (ctx) {
          // Resize the original image
          ctx.drawImage(img, 0, 0, adjustedWidth, adjustedHeight);
  
          // Convert resized image to a blob
          canvas.toBlob(async (resizedBlob) => {
            if (!resizedBlob) return;
  
            // Create a File object for the resized image
            const resizedImageFile = new File([resizedBlob], "resized_image.png", {
              type: "image/png",
            });
  
            // Resize the mask to align with the resized image
            const maskImg = new Image();
            maskImg.src = maskB64;
  
            maskImg.onload = () => {
              const maskCanvas = document.createElement("canvas");
              maskCanvas.width = adjustedWidth;
              maskCanvas.height = adjustedHeight;
  
              const maskCtx = maskCanvas.getContext("2d");
              if (maskCtx) {
                maskCtx.drawImage(maskImg, 0, 0, adjustedWidth, adjustedHeight);
                const alignedMaskB64 = maskCanvas.toDataURL();
  
                // Save the aligned mask without combining it with the image
                setMaskBase64(alignedMaskB64);
  
                // Keep the original file as the base image
                setFile(resizedImageFile);
  
                // Optional: Log debug info
                console.log("Adjusted Image Dimensions:", { width: adjustedWidth, height: adjustedHeight });
                console.log("Aligned Mask Base64:", alignedMaskB64);
              }
            };
          }, "image/png");
        }
      };
    }
  };
  
  

  
  

  const BrushIcon = createIcon({
    displayName: "BrushIcon",
    viewBox: "0 0 32 32",
    path: [
      <path
        d="M28.828 3.172a4.094 4.094 0 0 0-5.656 0L4.05 22.292A6.954 6.954 0 0 0 2 27.242V30h2.756a6.952 6.952 0 0 0 4.95-2.05L28.828 8.829a3.999 3.999 0 0 0 0-5.657zM10.91 18.26l2.829 2.829l-2.122 2.121l-2.828-2.828zm-2.619 8.276A4.966 4.966 0 0 1 4.756 28H4v-.759a4.967 4.967 0 0 1 1.464-3.535l1.91-1.91l2.829 2.828zM27.415 7.414l-12.261 12.26l-2.829-2.828l12.262-12.26a2.047 2.047 0 0 1 2.828 0a2 2 0 0 1 0 2.828z"
        fill="currentColor"
      />,
      <path
        d="M6.5 15a3.5 3.5 0 0 1-2.475-5.974l3.5-3.5a1.502 1.502 0 0 0 0-2.121a1.537 1.537 0 0 0-2.121 0L3.415 5.394L2 3.98l1.99-1.988a3.585 3.585 0 0 1 4.95 0a3.504 3.504 0 0 1 0 4.949L5.439 10.44a1.502 1.502 0 0 0 0 2.121a1.537 1.537 0 0 0 2.122 0l4.024-4.024L13 9.95l-4.025 4.024A3.475 3.475 0 0 1 6.5 15z"
        fill="currentColor"
      />,
    ],
  });
  
  

  return (
    <Flex
      direction={{ base: "column", lg: "row" }}
      w="100%"
      maxW="1400px"
      bg="transparent"
      borderRadius="12px"
      p={{ base: "0", md: "16px" }}
      gap={{ base: "12px", lg: "27px" }}
      alignItems="stretch" // Ensures both boxes are the same height
      justifyContent="space-between"
      mt="35px"
    >
      
      {/* Upload Box */}
      <Box
        position="relative"
        w={{ base: "100%", lg: "100%" }}
        h={preview ? "auto" : { base: "420px", md: "900px" }}
        bg="#000000"
        borderRadius="20px"
        display="flex"
        flexDirection="column"
        p={4}
        overflow="hidden"
        border="1px solid rgba(255, 255, 255, 0.5)"
        minH="300px"
        transition="height 0.3s ease-in-out"
        _before={{
          content: '""',
          position: "absolute",
          top: "-4px",
          left: "-4px",
          right: "-4px",
          bottom: "-4px",
          borderRadius: "16px",
          background: "linear-gradient(to right, #FFB5FF, #1AA5D3)",
          zIndex: -1,
        }}
      >
       {preview &&  <Flex pl="15px" pr="15px" pt="15px" w="100%" justify="space-between">
        <Button
 
 bg="transparent"
 minH="50px"
 minW="115px"
 color="white"
 fontSize="18px"
 fontWeight="medium"
 border="1px solid white"
 borderRadius="12px"
 p={0}
 pl="15px"
 pr="15px"
 display="flex"
 alignItems="center"
 gap="12px"
 _hover={{ opacity: 0.9, bg: "gray.600" }}
 onClick={() => {
   setPreview(null);
   setFile(null);
   setMaskBase64(null)
 }}
>
 <Icon as={IoMdClose} boxSize={6} />
 <Text>Close</Text>
</Button>
<Button

 bg="transparent"
 minH="50px"
 minW="115px"
 color="white"
 fontSize="18px"
 fontWeight="medium"
 border="1px solid white"
 borderRadius="12px"
 p={0}
 pl="15px"
 pr="15px"
 display="flex"
 alignItems="center"
 gap="12px"
 _hover={{ opacity: 0.9, bg: "gray.600" }}
 onClick={(e) => {
   e.stopPropagation(); // Prevent the dropzone's click event
   handleOpenMaskingTool(); // Open MaskingTool
 }}
 leftIcon={
   <BrushIcon boxSize={4} color="white" bg="transparent" />
 }
>
 Select
</Button>
</Flex>}
        <div
          {...getRootProps()}
          style={{
            width:  window.innerWidth < 768 ? "100%" : "95%",
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            border: "1px solid rgba(255, 255, 255, 0.5)",
            borderRadius: "12px",
            minHeight: preview ? "auto" :  window.innerWidth < 768 ? "350px" :"800px",
            transition: "height 0.3s ease-in-out",
            marginTop: window.innerWidth < 768 ? "20px" : "40px", 
            marginBottom: window.innerWidth < 768 ? "20px" : "0", // Add bottom margin for mobile
            marginLeft: window.innerWidth < 768 ? "0px" : "auto", // Reduce side margins for mobile
            marginRight: window.innerWidth < 768 ? "0px" : "auto", 
          }}
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <Text>Drop your image here!</Text>
          ) : preview ? (
            <>
              <Box position="relative"
                    display="flex"
                    justifyContent="center"
                    alignItems="flex-start"
                    w="100%"
                    h="100%"
                    >
              {/* Base Image */}
              <ChakraImage
                src={preview}
                alt="Preview Image"
                w="100%"
                h="100%"
                objectFit="contain"
                borderRadius="20px"
              />

              {/* Mask Overlay */}
              {maskBase64 && (
                <ChakraImage
                  src={maskBase64}
                  alt="Mask Overlay"
                  position="absolute"
                  top="0"
                  left="0"
                  w="100%"
                  h="100%"
                  objectFit="contain"
                  opacity="0.5" // Adjust transparency
                  pointerEvents="none"
                  style={{ clipPath: "inset(0)" }} 
                />
              )}
              
                
              </Box>
            </>
          ) : (
            <>
              <ChakraImage src={UploadRemixImageIcon.src} objectFit="contain" />
              <Text mt="20px" fontSize="18px" color="white" opacity="50%">
                Drag & Drop or Click to Upload Image
              </Text>
              <Button
                  mt="20px"
                  minH="50px"
                  minW={{ base: "250px", md: "300px" }}
                  bg="transparent"
                  color="white"
                  fontSize="18px"
                  border="1px solid white"
                  borderRadius="12px"
                  _hover={{ opacity: 0.9, bg: "gray.600" }}
                >
                  Upload Image
                </Button>
                <Button
                  mt="20px"
                  minH="50px"
                  minW={{ base: "250px", md: "300px" }}
                  bg="transparent"
                  color="white"
                  fontSize="18px"
                  border="1px solid white"
                  borderRadius="12px"
                  _hover={{ opacity: 0.9, bg: "gray.600" }}
                  onClick={(event) => {
                    event.stopPropagation(); // Prevent dropzone behavior
                    handleOpenGallery(); // Open the gallery modal
                  }}
                >
                  From Gallery
                </Button>
                {/* Remix Gallery Modal */}
                <RemixGalleryLayout
                  isAdmin={false} // Adjust based on admin requirements
                  isOpen={isGalleryOpen}
                  onClose={handleCloseGallery}
                  selectedImage={selectedImage}
                  setSelectedImage={(image) => {
                    setSelectedImage(image); // Set selected image
                    handleCloseGallery(); // Close the modal
                  }}
                />
            </>
          )}
        </div>
        {isMaskingToolOpen && preview && (
  <Modal isOpen={isMaskingToolOpen} onClose={() => setIsMaskingToolOpen(false)} size="full">
    <ModalOverlay bg="rgba(0, 0, 0, 0.9)" />
    <ModalContent bg="transparent" maxW="100vw" maxH="100vh">
      <ModalBody p={0}>
        <MaskingTool
          image={preview}
          mask = {maskBase64}
          onClose={(maskedImg: string | null, maskBase64: string | null) => {
            if (maskedImg) {
              setPreview(maskedImg);
            }
            setIsMaskingToolOpen(false);
            handleMaskingToolClose(maskedImg, maskBase64);
          }}
        />
      </ModalBody>
    </ModalContent>
  </Modal>
)}

        {preview && (
          <Box w="100%" mt={4}>
               <Flex direction="column" w="100%" p={{ base: "8px", md: "16px" }}>
              <Text color="#D5D5D5" fontSize="14px" fontWeight="bold" mb="6">
                Image Similarity
              </Text>
              <Slider
                step={0.05}
                min={0.05}
                max={1}
                value={strength}
                onChange={(value) => {
                  setStrength(value);
                  setGeneratedImageUrl(null); // ✅ reset on similarity slider change
                }}
              >
                <SliderMark
                  value={0}
                  textAlign="left"
                  bg="transparent"
                  color="white"
                  mt="3"
                  fontSize="sm"
                  ml="8"
                >
                  Image
                </SliderMark>
                <SliderMark
                  value={1}
                  textAlign="right"
                  bg="transparent"
                  color="white"
                  mt="3"
                  fontSize="sm"
                  ml="-50px"
                >
                  My Model
                </SliderMark>
                <SliderMark
                  value={strength}
                  textAlign="center"
                  bg="transparent"
                  color="white"
                  mt="-10"
                  ml="-6"
                  w="12"
                  fontSize="sm"
                >
                  {strength.toFixed(2)}
                </SliderMark>
                <SliderTrack bg="white" boxSize={2} borderRadius={4}>
                  <SliderFilledTrack bg="button.500" />
                </SliderTrack>
                <SliderThumb bg="white" boxSize={4} />
              </Slider>
            </Flex>

            <Flex
              direction="row"
              w="100%"
              alignItems="center"
              mt="20px"
              px={{ base: "8px", md: "16px" }}
            >
              <Text color="#D5D5D5" fontSize="14px" fontWeight="bold"  px={{ base: "2px", md: "0px" }} >
                Prompt (optional)
              </Text>
              <Button
                bg="transparent"
                color="white"
                size="sm"
                _hover={{}}
                _active={{}}
                onClick={() => setCustomPromptOpen(!customPromptOpen)}
              >
                {customPromptOpen ? "-" : "+"}
              </Button>
            </Flex>
            {customPromptOpen && (
              <Box w="100%" mt="10px" px={{ base: "0", md: "4" }} position="relative">
                <Textarea
                  value={prompt}
                  onChange={(e) => {
                    setPrompt(e.target.value);
                    setGeneratedImageUrl(null); // ✅ reset on prompt change
                  }}
                  borderRadius="17px"
                  minH="180px"
                  border="1px solid rgba(255, 255, 255, 0.5)"
                  placeholder="Describe what you want to see in the image..."
                  maxLength={500}
                  pr="40px" 
                />
                <Text
                  position="absolute"
                  color="gray"
                  bottom="10px"
                  right="20px"
                  fontSize="14px"
                >
                  {`${prompt.length}/500`}
                </Text>
              </Box>
            )}

      <Box mt={4} ml={{base: 3, md: 4 }}>
          <Text color="#D5D5D5" fontSize="14px" fontWeight="bold" mb={4}>
            Number of Generations
          </Text>
          <Flex gap={3}>
            {[1, 2, 3, 4].map((count) => (
              <Button
                key={count}
                size="sm"
                bg={numGenerations === count ? "linear-gradient(to right, #FFB5FF, #1AA5D3)" : "transparent"}
                color={numGenerations === count ? "black" : "white"}
                _hover={{
                  bg:
                    numGenerations === count
                      ? "linear-gradient(to right, #FFE1FF, #1AA5D3)"
                      : "rgba(255, 255, 255, 0.1)",
                }}
                borderRadius="full"
                border={
                  numGenerations === count
                    ? "none"
                    : "1px solid rgba(255, 255, 255, 0.5)"
                }
                onClick={() => {setNumGenerations(count);
                  setGeneratedImageUrl(null);
                }}
              >
                {count}
              </Button>
            ))}
          </Flex>
        </Box>


            {/* Base Lora Scale Box */}
            {/**<Box
              w="95%"
              mt={4}
              p={4}
              borderRadius="12px"
              border="1px solid rgba(255, 255, 255, 0.2)"
              bg="rgba(0, 0, 0, 0.3)"
            >
              <Flex align="center" gap={4}>
                <ChakraImage
                  src={model?.picture}
                  alt="Base Model"
                  boxSize="60px"
                  objectFit="cover"
                  borderRadius="8px"
                />
                <Box flex="1">
                  <Text color="white" mb={2} fontWeight="medium">
                    Base Model
                  </Text>
                  <Flex align="center" gap={2}>
                    <Text color="gray.300" fontSize="sm" minWidth="45px">
                      Scale:
                    </Text>
                    <Slider
                      aria-label="base-lora-scale"
                      defaultValue={1.0}
                      min={0}
                      max={1}
                      step={0.1}
                      value={baseLoraScale}
                      onChange={(value) => setBaseLoraScale(value)}
                      flex="1"
                    >
                      <SliderTrack bg="gray.600">
                        <SliderFilledTrack bg="#FF1B6D" />
                      </SliderTrack>
                      <SliderThumb boxSize={3} bg="white" />
                    </Slider>
                    <Text color="gray.300" fontSize="sm" minWidth="30px">
                      {baseLoraScale.toFixed(1)}
                    </Text>
                  </Flex>
                </Box>
              </Flex>
            </Box>
            
      
            {dynamicLoras.length > 0 && (
              <Box
                w="95%"
                mt={4}
                p={4}
                borderRadius="12px"
                border="1px solid rgba(255, 255, 255, 0.2)"
                bg="rgba(0, 0, 0, 0.3)"
              >
                <Flex align="center" gap={4}>
                  <ChakraImage
                    src={selectedLoraImage}
                    alt="Selected Lora"
                    boxSize="60px"
                    objectFit="cover"
                    borderRadius="8px"
                  />
                  <Box flex="1">
                    <Text color="white" mb={2} fontWeight="medium">
                      Style: {selectedLoraTitle}
                    </Text>
                    <Flex align="center" gap={2}>
                      <Text color="gray.300" fontSize="sm" minWidth="45px">
                        Scale:
                      </Text>
                      <Slider
                        aria-label="lora-scale"
                        defaultValue={0.5}
                        min={0}
                        max={1}
                        step={0.1}
                        value={dynamicLoras[0].weight}
                        onChange={(value) => {
                          const updatedLoras = [...dynamicLoras];
                          updatedLoras[0].weight = value;
                          setDynamicLoras(updatedLoras);
                        }}
                        flex="1"
                      >
                        <SliderTrack bg="gray.600">
                          <SliderFilledTrack bg="#FF1B6D" />
                        </SliderTrack>
                        <SliderThumb boxSize={3} bg="white" />
                      </Slider>
                      <Text color="gray.300" fontSize="sm" minWidth="30px">
                        {dynamicLoras[0].weight.toFixed(1)}
                      </Text>
                    </Flex>
                  </Box>
                  <Button
                    size="sm"
                    variant="ghost"
                    color="gray.400"
                    _hover={{ color: "white" }}
                    onClick={() => handleRemoveLora(0)}
                  >
                    <Icon as={IoMdClose} />
                  </Button>
                </Flex>
              </Box>
            )}
            
            
            <LoraSelector isFlux={true} onSelect={handleAddLora} /> */ }
            <Button
              w="100%"
              h="50px"
              bg="#FF1B6D"
              color="white"
              borderRadius="6px"
              fontWeight="bold"
              onClick={handleGenerateRemixImage}
              isDisabled={isInsufficientCredits}
              loadingText="Generating..."
              mt={4}
            >
              Create (1 credit)
            </Button>
          </Box>
        )}

      </Box>

      {/* Generated Image Display Box */}
      {/* Generated Image Display Box */}
      <Box
        position="relative"
        w={{ base: "100%", lg: "100%" }}
        h={preview ? "auto" : { base: "auto", md: "900px" }}
        bg="#000000"
        borderRadius="20px"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center" 
        p={{ base: 4, lg: 7 }}
        overflow="hidden"
        border="1px solid rgba(255, 255, 255, 0.5)"
        minH={{ base: "50px", md: "300px" }}
        transition="height 0.3s ease-in-out"
        _before={{
          content: '""',
          position: "absolute",
          top: "-4px",
          left: "-4px",
          right: "-4px",
          bottom: "-4px",
          borderRadius: "16px",
          background: "linear-gradient(to right, #FFB5FF, #1AA5D3)",
          zIndex: -1,
        }}
      >
        <AsyncDisplay
            modelId={model?.id || ""}
            isGenerating={activeRequests > 0}
            generatedData={{ image_urls: generatedImageUrl ? [generatedImageUrl] : [] }} // Pass URL directly
            numGenerations={numGenerations}
            activeRequests={activeRequests}
            setActiveRequests={setActiveRequests}
            
          />
      </Box>

    </Flex>
  );
};

export default FluxPlaygroundRemix;

